import { useMemo } from 'react';
import { useShowCause } from 'core/Api';
import { CauseLogo } from 'core/Components';
import { useBreakPoints, useCrowdFundingTranslations } from 'core/Hooks';
import { useAtomValue } from 'jotai';
import { Center, Group, Stack, Text } from 'ui';
import { ElementListSelf } from 'ui/Hooks';
import { CircleHeartIcon, PresentIcon } from 'ui/Icons';
import { donationCauseIdAtom } from '../../../Context';
import { cartIdAtom } from '../../../Context/store/store.provider';
import { useShowCart } from '../../../api/new/carts/get';

type Props = {
  setOnList?: (self: ElementListSelf) => () => void;
  elementList?: Map<any, ElementListSelf>;
  step?: number | string;
  causeId?: string;
};

export const PostDonationContinuationText = ({ step }: Props) => {
  const cartId = useAtomValue(cartIdAtom);
  const donationCauseId = useAtomValue(donationCauseIdAtom);
  const { data: cart } = useShowCart({ cartId });
  const { data: cause } = useShowCause(donationCauseId);
  const { t } = useCrowdFundingTranslations(donationCauseId);
  const { isSmallScreen } = useBreakPoints();

  const title = useMemo(() => {
    if (step === 1) {
      return cart?.recurring
        ? `Finish Creating an Account to Manage Your Monthly ${t('Donation')}`
        : 'Finish Joining Givebacks To Support Us Through Everyday Purchases';
    } else {
      return `Get the Extension to Support ${cause?.name ?? 'your cause'} When You Shop Online`;
    }
  }, [step, cart, cause]);

  return (
    <Stack gap={isSmallScreen ? 10 : 'xl'}>
      <Group gap="lg">
        {!isSmallScreen && <CauseLogo size="lg" cause_uuid={donationCauseId} />}
        <Text fz={{ base: 'lg', sm: '24px' }} fw={600} c="white">
          {title}
        </Text>
      </Group>
      <Stack>
        <Group gap={isSmallScreen ? 'xs' : 'sm'} wrap="nowrap">
          <Center style={{ flexShrink: 0 }} c="tangerine">
            <CircleHeartIcon />
          </Center>
          <Text fz={{ base: 'xs', sm: 'md' }} fw={500} c="white">
            {step === 1 ? (
              <span>
                {`Support ${
                  cause?.name ?? 'your cause'
                } whenever you shop or dine at over 25,000 merchants`}{' '}
                <b>at no extra cost to you.</b>
              </span>
            ) : (
              `Givebacks automatically ${t(
                'donates'
              )} whenever you make a qualifying purchase at a participating online merchant.`
            )}
          </Text>
        </Group>
        <Group gap="sm" wrap="nowrap">
          <Center style={{ flexShrink: 0 }} c="tangerine">
            <PresentIcon />
          </Center>
          <Text fz={{ base: 'xs', sm: 'md' }} fw={400} c="white">
            {step === 1
              ? `Make & manage direct & recurring ${t('donations')}.`
              : `Shop on your desktop at over 15,000 merchants and earn free ${t(
                  'donations'
                )} for ${cause?.name ?? 'your cause'}.`}
          </Text>
        </Group>
      </Stack>
    </Stack>
  );
};
